import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import GlobalSearchService, { SearchResult, TrendingItem, SearchCategory } from '@/services/GlobalSearchService';

interface GlobalSearchState {
  // State
  query: string;
  results: SearchResult[];
  recentSearches: SearchResult[];
  trending: TrendingItem[];
  loading: boolean;
  error: string | null;
  isOpen: boolean;
  userRole: string | null;

  // Actions
  setQuery: (query: string) => void;
  setIsOpen: (isOpen: boolean) => void;
  setUserRole: (role: string) => void;
  search: (categories?: SearchCategory[]) => Promise<void>;
  addRecentSearch: (result: SearchResult) => void;
  clearRecentSearches: () => void;
  loadTrending: () => Promise<void>;
  reset: () => void;
}

const MAX_RECENT_SEARCHES = 5;

export const useGlobalSearchStore = create<GlobalSearchState>()(
  persist(
    (set, get) => ({
      // Initial State
      query: '',
      results: [],
      recentSearches: [],
      trending: [],
      loading: false,
      error: null,
      isOpen: false,
      userRole: null,

      // Set query
      setQuery: (query: string) => {
        set({ query });
      },

      // Toggle search modal
      setIsOpen: (isOpen: boolean) => {
        set({ isOpen });
        // Reset query and results when closing
        if (!isOpen) {
          set({ query: '', results: [] });
        }
      },

      // Set user role
      setUserRole: (role: string) => {
        set({ userRole: role });
      },

      // Perform search
      search: async (categories?: SearchCategory[]) => {
        const { query, userRole } = get();

        if (query.trim().length < 2) {
          set({ results: [] });
          return;
        }

        set({ loading: true, error: null });

        const role = userRole || 'trainee';

        try {
          // If no categories specified, try the API first
          if (!categories || categories.length === 0) {
            const response = await GlobalSearchService.search(query);

            if (response.success && response.data) {
              // Get quick access pages from service
              const quickPages = GlobalSearchService.getQuickAccessPages(role);

              // Filter quick pages that match the query
              const matchingPages = quickPages.filter(page =>
                page.title.toLowerCase().includes(query.toLowerCase()) ||
                page.description?.toLowerCase().includes(query.toLowerCase())
              );

              // Combine API results with matching pages
              const combinedResults = [
                ...response.data.results,
                ...matchingPages
              ];

              set({
                results: combinedResults,
                loading: false,
              });
            } else {
              // If API fails, fall back to quick pages only
              const quickPages = GlobalSearchService.getQuickAccessPages(role);

              const matchingPages = quickPages.filter(page =>
                page.title.toLowerCase().includes(query.toLowerCase()) ||
                page.description?.toLowerCase().includes(query.toLowerCase())
              );

              set({
                results: matchingPages,
                loading: false,
                error: response.error?.message || null,
              });
            }
          } else {
            // Search specific categories
            const response = await GlobalSearchService.search(query, categories);

            if (response.success && response.data) {
              set({
                results: response.data.results,
                loading: false,
              });
            } else {
              set({
                results: [],
                loading: false,
                error: response.error?.message || null,
              });
            }
          }
        } catch (error: any) {
          // Fallback to quick pages on error
          const quickPages = GlobalSearchService.getQuickAccessPages(role);

          const matchingPages = quickPages.filter(page =>
            page.title.toLowerCase().includes(query.toLowerCase()) ||
            page.description?.toLowerCase().includes(query.toLowerCase())
          );

          set({
            results: matchingPages,
            loading: false,
            error: 'Search service unavailable. Showing quick access pages.',
          });
        }
      },

      // Add to recent searches
      addRecentSearch: (result: SearchResult) => {
        const { recentSearches } = get();

        // Remove duplicates and add to beginning
        const filtered = recentSearches.filter(r => r.id !== result.id);
        const updated = [result, ...filtered].slice(0, MAX_RECENT_SEARCHES);

        set({ recentSearches: updated });
      },

      // Clear recent searches
      clearRecentSearches: () => {
        set({ recentSearches: [] });
      },

      // Load trending items
      loadTrending: async () => {
        try {
          const trending = await GlobalSearchService.getTrending();
          set({ trending });
        } catch (error) {
          console.error('Failed to load trending items:', error);
          set({ trending: [] });
        }
      },

      // Reset search state
      reset: () => {
        set({
          query: '',
          results: [],
          loading: false,
          error: null,
        });
      },
    }),
    {
      name: 'global-search-storage',
      // Only persist recent searches
      partialize: (state) => ({
        recentSearches: state.recentSearches,
      }),
    }
  )
);
